import React from 'react';
import { useTheme } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';
import { CANGURO_LOGO_BLACK_URL } from '../data';

const mediaMix = [
  {
    icon: (
      <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
    title: 'A. Pantalla y señal en vivo',
    subtitle:
      'Micros en rotativa (30–45 s, Tips Canguro) e inserts gráficos en vivo: lower thirds con CTA y códigos QR dinámicos para guías web.',
  },
  {
    icon: (
      <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    title: 'B. Tres programas selectivos',
    subtitle:
      'TNO Business (productividad y flotas), Franquicias en Venezuela (retail y experiencia en tienda) y Noticias TNO (cápsula tech global y mercado local).',
  },
  {
    icon: (
      <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    title: 'C. Estrategia editorial (SEO)',
    subtitle:
      'TNO Digest: 2 artículos semanales (8 al mes) en tnonetwork.com para captar búsquedas orgánicas sobre problemas técnicos y decisiones de compra.',
  },
  {
    icon: (
      <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2m0 2a2 2 0 100 4m0-4a2 2 0 110 4M7 16v-2m0 2a2 2 0 100 4m0-4a2 2 0 110 4m10-8V6m0 2a2 2 0 100 4m0-4a2 2 0 110 4m0 6v-2m0 2a2 2 0 100 4m0-4a2 2 0 110 4" />
      </svg>
    ),
    title: 'D. Entorno digital (redes)',
    subtitle:
      'Clips co-acreditados en Instagram y TikTok (TNO × Canguro) y carruseles informativos que refuerzan los artículos del Digest.',
  },
];

const AboutSection = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

  return (
    <Box>
      <Box marginBottom={{ xs: 4, sm: 8, md: 10 }}>
        <Typography
          sx={{ textTransform: 'uppercase', fontWeight: 600 }}
          gutterBottom
          color="primary"
          align="center"
        >
          2. Mix de medios
        </Typography>
        <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
          Multiplataforma 360°
        </Typography>
        <Typography variant="h6" component="p" color="textSecondary" align="center" maxWidth={720} margin="0 auto">
          Cobertura audiovisual, programación selectiva, autoridad editorial y presencia en redes — en un solo
          despliegue mensual continuo.
        </Typography>
      </Box>
      <Grid container spacing={4} direction={isMd ? 'row' : 'column-reverse'}>
        <Grid item xs={12} md={6} data-aos={isMd ? 'fade-right' : 'fade-up'}>
          <Typography
            sx={{ textTransform: 'uppercase', fontWeight: 600 }}
            gutterBottom
            color="primary"
          >
            Pilares del mix
          </Typography>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Señal, programas, Digest y redes
          </Typography>
          <List disablePadding>
            {mediaMix.map((item, index) => (
              <ListItem key={index} disableGutters sx={{ alignItems: 'flex-start' }} data-aos="fade-up">
                <ListItemAvatar>
                  <Box
                    component={Avatar}
                    variant="rounded"
                    bgcolor={`${theme.palette.primary.light}55`}
                    color={theme.palette.secondary.main}
                  >
                    {item.icon}
                  </Box>
                </ListItemAvatar>
                <ListItemText
                  primary={item.title}
                  secondary={item.subtitle}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            ))}
          </List>
        </Grid>
        <Grid item xs={12} md={6} container justifyContent="center" alignItems="center" data-aos={isMd ? 'fade-left' : 'fade-up'}>
          <Box
            component={Card}
            boxShadow={4}
            borderRadius={2}
            overflow="hidden"
            sx={{ bgcolor: theme.palette.alternate.main, width: '100%' }}
          >
            <Box p={3} textAlign="center">
              <Box
                component="img"
                src={CANGURO_LOGO_BLACK_URL}
                alt="Logo Canguro"
                sx={{
                  width: '100%',
                  maxWidth: 280,
                  height: 'auto',
                  maxHeight: 88,
                  mb: 2,
                  mx: 'auto',
                  display: 'block',
                  objectFit: 'contain',
                }}
              />
              <Typography variant="overline" color="primary" fontWeight={700}>
                Cliente
              </Typography>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Canguro
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Más de 160 puntos de venta en Venezuela. Tecnología, accesorios y conectividad para el consumidor
                y el canal corporativo.
              </Typography>
              <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', textAlign: 'left' }}>
                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                  Responsabilidad TNO
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Conceptualización, edición, locución y postproducción de micros, inserts gráficos y redacción SEO
                  del portal.
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" gutterBottom sx={{ mt: 2 }}>
                  Responsabilidad Canguro
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Manual de identidad, logotipos vectorizados y activos gráficos para el desarrollo visual de la campaña.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AboutSection;
